import { useEffect, useState, useCallback } from "react";
import { Share2, Download, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Exact share copy supplied by the product owner. Kept verbatim — DO NOT
// edit without explicit approval, since this is brand-defining language.
const SHARE_TEXT = `🕌 EasyQuran.UK

A new way to connect with the Quran.

Recite any verse and let AI Voice Sense technology instantly explain its meaning in English or Urdu. I'm sure you'll love experiencing and understanding the Quran in this way.

Try it today and share it with others.

🌐 EasyQuran.UK`;

const SHARE_URL = "https://easyquran.uk";

// Share button — uses the native Web Share API on phones (opens WhatsApp /
// iMessage / Messenger / etc. directly with one tap) and falls back to
// copy-to-clipboard on desktop where Web Share isn't supported.
export const ShareAppButton = ({ variant = "default" }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = useCallback(async () => {
    const payload = { text: SHARE_TEXT, url: SHARE_URL };
    // Web Share API — present on iOS Safari, Android Chrome, modern Edge.
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (e) {
        // User cancelled the share sheet — silent.
        if (e?.name === "AbortError") return;
      }
    }
    // Fallback — copy the full message+URL to clipboard.
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT}\n${SHARE_URL}`);
      setCopied(true);
      toast.success("Copied! Paste it in WhatsApp or anywhere.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't share. Please copy this URL manually: " + SHARE_URL);
    }
  }, []);

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handleShare}
        data-testid="share-app-icon-btn"
        aria-label="Share EasyQuran"
        title="Share EasyQuran"
        className="inline-flex items-center justify-center w-9 h-9 rounded-full text-foreground/75 hover:text-primary hover:bg-muted transition-colors"
      >
        {copied ? <Check className="w-4 h-4 text-primary" /> : <Share2 className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      data-testid="share-app-btn"
      className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                 bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-800
                 text-white font-medium shadow-md ring-1 ring-amber-300/60
                 hover:ring-amber-300 hover:shadow-lg transition-all duration-200 ease-out
                 hover:scale-[1.02]"
    >
      {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
      <span>{copied ? "Copied!" : "Share with friends & family"}</span>
    </button>
  );
};

// Install banner — listens for the `beforeinstallprompt` event (Android
// Chrome / Edge). On iOS Safari that event never fires, so we show a tiny
// "Add to Home Screen" hint with the share-icon picture instead.
const DISMISS_KEY = "eq_pwa_install_dismissed_at";

export const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Skip if user already installed (display-mode standalone) or recently dismissed.
    if (window.matchMedia?.("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return; // iOS PWA flag
    const last = Number(localStorage.getItem(DISMISS_KEY)) || 0;
    if (Date.now() - last < 1000 * 60 * 60 * 24 * 14) return; // 2-week cool-down

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS fallback — show after a short dwell so it doesn't fire immediately.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      const t = setTimeout(() => setIosHint(true), 18_000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onPrompt);
        clearTimeout(t);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") toast.success("Welcome to the EasyQuran app!");
    setShow(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setIosHint(false);
  };

  if (show && deferredPrompt) {
    return (
      <div
        className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[min(420px,calc(100vw-16px))]
                   rounded-2xl border border-accent/40 bg-card shadow-2xl p-3.5 flex items-start gap-3
                   animate-fade-in-up"
        data-testid="pwa-install-prompt"
      >
        <span className="w-10 h-10 rounded-xl bg-emerald-600 text-white inline-flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-tight">Install EasyQuran on your phone</p>
          <p className="text-[12px] text-foreground/65 leading-snug mt-0.5">
            One-tap install · Works offline · Free
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-accept"
              className="h-8 px-3 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="pwa-install-dismiss"
              className="h-8 px-3 rounded-full bg-muted text-foreground/75 text-xs hover:bg-muted/80"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-foreground/40 hover:text-foreground p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  if (iosHint) {
    return (
      <div
        className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[min(420px,calc(100vw-16px))]
                   rounded-2xl border border-accent/40 bg-card shadow-2xl p-3.5 flex items-start gap-3
                   animate-fade-in-up"
        data-testid="pwa-ios-hint"
      >
        <span className="w-10 h-10 rounded-xl bg-emerald-600 text-white inline-flex items-center justify-center shrink-0">
          <Download className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm leading-tight">Install EasyQuran on your iPhone</p>
          <p className="text-[12px] text-foreground/65 leading-snug mt-0.5">
            Tap the <Share2 className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> Share icon in Safari, then <strong>“Add to Home Screen”</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-foreground/40 hover:text-foreground p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return null;
};
